<template>
  <div class="product-variants">
    <div
      v-if="!product.giftCard"
      class="product-variants__header"
    >
      <div
        id="product-variants-size"
        class="product-variants__subheader"
      >
        <p
          v-if="!product.hasOnlyDefaultVariant"
          class="product-variants__option text-body-xs text-body-s-desktop"
          v-text="headerText"
        />

        <transition name="fade">
          <p
            v-if="variant"
            class="product-variants__inventory text-body-xs text-body-s-desktop"
            :class="`product-variants__inventory--${inventoryState?.state}`"
            v-text="inventoryState?.label"
          />
        </transition>
      </div>

      <button
        v-if="matchedBrand && showSizeGuide"
        class="
          product-variants__size-guide
          text-body-xs-link
          text-body-s-link-desktop
        "
        type="button"
        @click.prevent="handleSizeGuide"
        v-text="$string('product.form.size_guide')"
      />
    </div>

    <div
      v-show="product.variants?.length > 1"
      class="product-variants__options"
    >
      <template
        v-for="(variantObject) of variants"
        :key="`variant-${variantObject.id}`"
      >
        <input
          :id="`variant-${variantObject.id}`"
          v-model="currentVariantId"
          class="product-variants__input visually-hidden"
          name="id"
          type="radio"
          :value="variantObject.id"
        >

        <label
          class="
            product-variants__label
            button
            button--swatch
            button--block
            text-center
          "
          :class="{ 'product-variants__label--sold-out': !variantObject.available }"
          :for="`variant-${variantObject.id}`"
          v-html="getVariantTitle(variantObject)"
        />
      </template>
    </div>

    <!-- Loading state -->
    <div
      v-if="!product"
      class="product-variants__options"
    >
      <template
        v-for="(_, index) of [...Array(9)]"
        :key="`variant-loading-${index}`"
      >
        <input
          :id="`variant-${index}`"
          class="product-variants__input visually-hidden"
          name="id"
          type="radio"
          :value="index"
        >

        <label
          class="
            product-variants__label
            button
            button--swatch
            button--block
            text-center
          "
          :for="`variant-${index}`"
          v-text="$string('accessibility.loading')"
        />
      </template>
    </div>
  </div>
</template>

<script>

/**
 * Vue: Product variants (product-variants)
 * -----------------------------------------------------------------------------
 * Variant selector for product form.
 *
 * @param {Number} lowStockThreshold - Low stock warning threshold.
 * @param {Object|Boolean} matchedBrand - Matched brand for size guide.
 * @param {Object} product - Liquid local product object.
 * @param {Boolean} showSizeGuide - If size guide should be displayed.
 * @param {Object|Boolean} variant - Canvas formatted variant object.
 *
 */
import { mapActions } from 'vuex'

export default {
  name: 'ProductVariants',

  props: {
    lowStockThreshold: {
      type: Number,
      default: 5,
    },
    matchedBrand: {
      type: [Boolean, Object],
      default: () => ({}),
    },
    product: {
      type: [Boolean, Object],
      default: () => ({
        hasOnlyDefaultVariant: false,
        variants: [],
      }),
      required: true,
    },
    showSizeGuide: {
      type: Boolean,
      default: true,
    },
    variant: {
      type: [Boolean, Object],
      default: false,
      required: true,
    },
  },

  emits: {
    'update-variant': (payload) => typeof payload === 'object',
  },

  data() {
    return {
      currentVariantId: this.variant?.id || 0,
      variants: [],
    }
  },

  computed: {

    /**
     * Compute header text.
     * @returns {String}
     */
    headerText() {
      if (this.product.variants?.length === 1) {
        return this.product.variants[0].title.includes('/')
          ? this.product.variants[0].title.split('/')[1].trim()
          : this.product.variants[0].title
      }

      return this.$string('product.form.size')
    },

    /**
     * Compute stock message and indicator.
     * @returns {Object}
     */
    inventoryState() {
      let label = this.$string('product.form.in_stock')
      let state = 'available'

      /**
       * Handle sold out but continue selling products.
       */
      if (this.variant.inventory <= 0 && this.variant.available) {
        return {
          label,
          state,
        }
      }

      if (this.variant.inventory <= this.lowStockThreshold) {
        label = this.variant.inventory <= 0
          ? this.$string('product.form.sold_out')
          : this.$string(
            'product.form.low_stock',
            { replace: { count: this.variant.inventory } },
          )

        state = this.variant.inventory <= 0 ? 'out' : 'low'
      }

      return {
        label,
        state,
      }
    },
  },

  watch: {

    /**
     * Watch currentVariantId to update Canvas variant when select changes.
     */
    currentVariantId() {
      if (this.variant.id === this.currentVariantId) {
        return
      }

      const variant = this.getVariantFromId(this.currentVariantId)

      if (!variant) {
        return
      }

      this.$emit('update-variant', variant)
    },

    /**
     * Watch variant, if it resets to false then update current variant ID.
     */
    variant() {
      if (this.variant) {
        return
      }

      this.currentVariantId = 0
    },
  },

  mounted() {
    this.buildVariants()

    /**
     * Get variant from URL if parameters exist.
     */
    const variant = this.getVariantFromUrl()

    if (variant) {
      this.currentVariantId = variant.id
      return
    }

    /**
     * Otherwise select first variant if single variant product.
     */
    if (this.product.variants?.length !== 1) {
      return
    }

    this.currentVariantId = this.product.variants[0].id
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      openOverlay: 'overlays/open',
    }),

    /**
     * Build variants by the size order theme settings.
     */
    buildVariants() {
      if (!this.product) {
        return
      }

      const sizeOptionNumber = this.product.options.findIndex((option) => {
        return option.name.toLowerCase() === 'size'
      })

      if (sizeOptionNumber < 0 || !cnvs.settings.sizePdpOrder) {
        this.variants = this.product.variants
        return
      }

      let sizeValues = this.product.options[sizeOptionNumber].values
      if (sizeValues.length === 1) {
        this.variants = this.product.variants
        return
      }

      sizeValues = this.reorderSizeValues(sizeValues)

      const variants = []
      sizeValues.forEach((value) => {
        const tempVariant = this.product.variants.find((variant) => {
          return value === variant.selectedOptions[sizeOptionNumber].value
        })
        variants.push(tempVariant)
      })
      this.variants = variants
    },

    /**
     * Reorder size values from the size order theme settings.
     * @param {Array} sizeValues - Size options to be sorted.
     * @returns {Array}
     */
    reorderSizeValues(sizeValues) {
      const manualOrders = cnvs.settings.sizePdpOrder.split(',')
        .map((size) => size.toLowerCase().trim())
      const values = sizeValues.sort((a, b) => {
        const indexOfA = manualOrders.indexOf(a.toLowerCase())
        const indexOfB = manualOrders.indexOf(b.toLowerCase())

        /**
         * Neither is in the order array; retain their relative positions
         */
        if (indexOfA === -1 && indexOfB === -1) {
          return 0
        }

        /**
         * Only b is in the order array; place b first
         */
        if (indexOfA === -1) {
          return 1
        }

        /**
         * Only a is in the order array; place a first
         */
        if (indexOfB === -1) {
          return -1
        }

        /**
         * Both are in the order array; order by their positions
         */
        return indexOfA - indexOfB
      })

      return values
    },

    /**
     * Get initial variant from URL parameter.
     * @returns {Object}
     */
    getVariantFromUrl() {
      const params = new URLSearchParams(document.location.search.substring(1))
      const variantId = Number(params.get('variant'))
      return this.getVariantFromId(variantId)
    },

    /**
     * Get variant object from variant ID.
     * @returns {Object}
     */
    getVariantFromId(variantId) {
      return this.product.variants?.find((variant) => {
        return variant.id === variantId
      })
    },

    /**
     * Get variant title HTML.
     * - Spacing added around title to extend strikethrough when sold out.
     * @param {Object} variant - Variant object.
     * @returns {String}
     */
    getVariantTitle(variant) {
      let title = variant.title.includes('/')
        ? variant.title
          .split('/')[1]
          .trim()
        : variant.title

      if (this.product.giftCard) {
        title = this.$formatMoney(variant.price.amount)
      }

      if (!variant.available) {
        return `
          &nbsp;${title}&nbsp;

          <span class="visually-hidden">
            ${this.$string('product.form.sold_out')}
          </span>
        `
      }

      return ` ${title} `
    },

    /**
     * Handle size guide click.
     */
    handleSizeGuide() {
      this.openOverlay({
        component: 'size-guide-tables',
        ignoreDismissed: true,
        namespace: 'sizeGuide',
      })
    },
  },
}
</script>

<style lang="scss">
@import './product-variants';
</style>
